export const ANSWER_WIDGET_FENCE_LANG = 'openx-widget';

const MAX_CHART_SERIES = 2;
const MIN_CHART_POINTS = 3;
const MAX_DAILY_CHART_POINTS = 180;

export type AnswerWidgetCellValue = string | number | null;

export type AnswerWidgetGranularity = 'day' | 'hour';

export type AnswerWidgetMetricType = 'volume' | 'unit' | 'rate';

export type AnswerWidgetColumn = {
  key: string;
  label: string;
  align?: 'left' | 'right';
  metricType?: AnswerWidgetMetricType;
};

export type AnswerWidgetSeries = {
  key: string;
  label?: string;
  metricType: AnswerWidgetMetricType;
  kind: 'bar' | 'line';
  axis: 'left' | 'right';
};

export type AnswerWidgetChartConfig = {
  x: string;
  series: AnswerWidgetSeries[];
  leftLabel?: string;
  rightLabel?: string;
};

export type AnswerWidgetPayload = {
  id: string;
  title: string;
  columns: AnswerWidgetColumn[];
  rows: Record<string, AnswerWidgetCellValue>[];
  granularity?: AnswerWidgetGranularity;
  timeKey?: string;
  chart?: AnswerWidgetChartConfig;
  defaultView: 'table' | 'chart';
  pageSize?: number;
};

export type ParsedAnswerWidget = { ok: true; value: AnswerWidgetPayload } | { ok: false; error: string };

type AnswerWidgetRow = Record<string, AnswerWidgetCellValue>;

type SeriesInput = Pick<AnswerWidgetSeries, 'key' | 'label' | 'metricType'>;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function normalizeColumn(raw: unknown): AnswerWidgetColumn | null {
  if (!isRecord(raw)) return null;

  const key = asNonEmptyString(raw.key);
  if (key === null) return null;

  return {
    key,
    label: asNonEmptyString(raw.label) ?? key,
    align: raw.align === 'right' ? 'right' : 'left',
    metricType: normalizeMetricType(raw.metricType) ?? undefined,
  };
}

function normalizeCell(value: unknown): AnswerWidgetCellValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' || typeof value === 'boolean') return String(value);
  return null;
}

function normalizeRow(raw: unknown, columns: AnswerWidgetColumn[]): AnswerWidgetRow | null {
  if (!isRecord(raw)) return null;

  const row: AnswerWidgetRow = {};
  for (const column of columns) {
    row[column.key] = normalizeCell(raw[column.key]);
  }
  return row;
}

function normalizeGranularity(value: unknown): AnswerWidgetGranularity | undefined {
  return value === 'day' || value === 'hour' ? value : undefined;
}

function normalizeMetricType(value: unknown): AnswerWidgetMetricType | null {
  return value === 'volume' || value === 'unit' || value === 'rate' ? value : null;
}

function isNumericSeries(key: string, rows: AnswerWidgetRow[]): boolean {
  let hasValue = false;

  for (const row of rows) {
    const value = row[key];
    if (value === null) continue;
    if (typeof value !== 'number') return false;
    hasValue = true;
  }

  return hasValue;
}

function normalizeSeries(
  raw: unknown,
  columnsByKey: Map<string, AnswerWidgetColumn>,
  rows: AnswerWidgetRow[]
): SeriesInput | null {
  if (!isRecord(raw)) return null;

  const key = asNonEmptyString(raw.key);
  const column = key === null ? undefined : columnsByKey.get(key);
  if (key === null || column === undefined || !isNumericSeries(key, rows)) return null;

  const metricType = column.metricType ?? normalizeMetricType(raw.metricType) ?? null;
  if (metricType === null) return null;

  return { key, label: asNonEmptyString(raw.label) ?? undefined, metricType };
}

function assignSeriesRoles(series: SeriesInput[]): AnswerWidgetSeries[] {
  if (series.length === 1) {
    const [only] = series;
    return [{ ...only, kind: only.metricType === 'volume' ? 'bar' : 'line', axis: 'left' }];
  }

  const barIndex = Math.max(
    series.findIndex(entry => entry.metricType === 'volume'),
    0
  );

  return series.map((entry, index) => ({
    ...entry,
    kind: index === barIndex ? 'bar' : 'line',
    axis: index === barIndex ? 'left' : 'right',
  }));
}

function normalizeChart(
  raw: unknown,
  columns: AnswerWidgetColumn[],
  rows: AnswerWidgetRow[],
  granularity: AnswerWidgetGranularity | undefined
): AnswerWidgetChartConfig | undefined {
  if (!isRecord(raw) || granularity === undefined) return undefined;

  const columnsByKey = new Map(columns.map(column => [column.key, column]));

  const x = asNonEmptyString(raw.x);
  if (x === null || !columnsByKey.has(x)) return undefined;

  const points = new Set(rows.map(row => row[x]).filter(value => value !== null)).size;
  if (points < MIN_CHART_POINTS) return undefined;
  if (granularity === 'day' && points > MAX_DAILY_CHART_POINTS) return undefined;

  const declared = Array.isArray(raw.series) ? raw.series : [];
  if (declared.length === 0 || declared.length > MAX_CHART_SERIES) return undefined;

  const series: SeriesInput[] = [];
  for (const entry of declared) {
    const parsed = normalizeSeries(entry, columnsByKey, rows);
    if (parsed !== null && !series.some(existing => existing.key === parsed.key)) {
      series.push(parsed);
    }
  }

  if (series.length === 0) return undefined;

  return {
    x,
    series: assignSeriesRoles(series),
    leftLabel: asNonEmptyString(raw.leftLabel) ?? undefined,
    rightLabel: asNonEmptyString(raw.rightLabel) ?? undefined,
  };
}

function fallbackTitle(chart: AnswerWidgetChartConfig | undefined, columns: AnswerWidgetColumn[]): string {
  if (chart === undefined) {
    return columns
      .filter(column => column.metricType !== undefined)
      .map(column => column.label)
      .join(' / ');
  }

  const axisLabel = { left: chart.leftLabel, right: chart.rightLabel };
  return (['left', 'right'] as const)
    .flatMap(axis => chart.series.filter(series => series.axis === axis))
    .map(series => axisLabel[series.axis] ?? series.label ?? columns.find(column => column.key === series.key)?.label)
    .join(' / ');
}

function normalizePayload(raw: unknown): ParsedAnswerWidget {
  if (!isRecord(raw)) {
    return { ok: false, error: 'Widget payload is not an object.' };
  }

  const id = asNonEmptyString(raw.id);
  if (id === null) {
    return { ok: false, error: 'Widget payload is missing an id.' };
  }

  const columns = Array.isArray(raw.columns)
    ? raw.columns
        .map(normalizeColumn)
        .filter((c): c is AnswerWidgetColumn => c !== null)
        .filter((column, index, all) => all.findIndex(other => other.key === column.key) === index)
    : [];

  if (columns.length === 0) {
    return { ok: false, error: `Widget "${id}" declares no usable columns.` };
  }

  const rows = Array.isArray(raw.rows)
    ? raw.rows.map(entry => normalizeRow(entry, columns)).filter((r): r is AnswerWidgetRow => r !== null)
    : [];

  const granularity = normalizeGranularity(raw.granularity);
  const chart = normalizeChart(raw.chart, columns, rows, granularity);
  const declaredTimeKey = asNonEmptyString(raw.timeKey);
  const timeKey =
    declaredTimeKey !== null && columns.some(column => column.key === declaredTimeKey) ? declaredTimeKey : chart?.x;
  const pageSizeRaw = typeof raw.pageSize === 'number' ? Math.floor(raw.pageSize) : NaN;

  return {
    ok: true,
    value: {
      id,
      title: asNonEmptyString(raw.title) ?? fallbackTitle(chart, columns),
      columns,
      rows,
      granularity,
      timeKey,
      chart,
      defaultView: chart === undefined ? 'table' : 'chart',
      pageSize: Number.isFinite(pageSizeRaw) && pageSizeRaw > 0 ? Math.min(pageSizeRaw, 200) : 10,
    },
  };
}

export function parseAnswerWidgetPayload(raw: unknown, onError?: (error: string) => void): ParsedAnswerWidget {
  const parsed = normalizePayload(raw);
  if (!parsed.ok) onError?.(parsed.error);
  return parsed;
}

export function parseAnswerWidgetMarker(body: string): string | null {
  const trimmed = body.trim();
  if (trimmed.length === 0) return null;

  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return isRecord(parsed) ? asNonEmptyString(parsed.id) : null;
    } catch {
      return null;
    }
  }

  return /^[\w.:-]+$/.test(trimmed) ? trimmed : null;
}
